import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowIcon, CloseIcon, GlobeIcon, MenuIcon, MoonIcon, PhoneIcon, SunIcon } from './Icons'
import { Logo } from './Logo'
import { copy, services, type Locale } from '../content/site'

export type Theme = 'light' | 'dark'

type Props = {
  locale: Locale
  onLocaleChange: (locale: Locale) => void
  theme: Theme
  onThemeChange: (theme: Theme) => void
}

const sectionIds = ['services', 'work', 'process', 'about', 'location'] as const

export function SiteHeader({ locale, onLocaleChange, theme, onThemeChange }: Props) {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const t = copy[locale]
  const homeLink = (hash: string) => `/${hash}`
  const currentItem = location.pathname === '/company/registration' ? 'registration' : location.pathname === '/' ? activeSection : null

  useEffect(() => {
    if (location.pathname !== '/') return
    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        let current: string | null = null
        for (const id of sectionIds) {
          const section = document.getElementById(id)
          if (section && section.getBoundingClientRect().top <= 250) current = id
        }
        setActiveSection(current)
      })
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [location.pathname, location.hash])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setMenuOpen(false)
      setServicesOpen(false)
    })
    return () => cancelAnimationFrame(frame)
  }, [location.pathname, location.hash])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        setServicesOpen(false)
        }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const closeMenu = () => setMenuOpen(false)
  const themeLabel = locale === 'fa' ? 'تغییر حالت روشن و تاریک' : 'Toggle light and dark mode'
  const themeControl = (
    <button
      className="theme-switch"
      type="button"
      aria-label={themeLabel}
      aria-pressed={theme === 'dark'}
      onClick={() => onThemeChange(theme === 'light' ? 'dark' : 'light')}
    >
      <span className={`theme-switch__option ${theme === 'light' ? 'is-active' : ''}`}><SunIcon /></span>
      <span className={`theme-switch__option ${theme === 'dark' ? 'is-active' : ''}`}><MoonIcon /></span>
    </button>
  )

  return (
    <>
      <header className="site-header liquid-glass" onMouseLeave={() => setServicesOpen(false)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setServicesOpen(false) }}>
        <Link className="header-brand" to="/" onClick={closeMenu}><Logo locale={locale} /></Link>
        <nav
          className="desktop-nav"
          aria-label={locale === 'fa' ? 'ناوبری اصلی' : 'Primary navigation'}
        >
          <a
            className={currentItem === 'services' ? 'is-active' : ''}
            data-nav-id="services"
            aria-current={currentItem === 'services' ? 'location' : undefined}
            aria-haspopup="true"
            aria-controls="services-dropdown"
            aria-expanded={servicesOpen}
            href={homeLink('#services')}
            onMouseEnter={() => setServicesOpen(true)}
            onFocus={() => setServicesOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setServicesOpen(true)
                requestAnimationFrame(() => document.querySelector<HTMLElement>('.services-popover__grid a')?.focus())
              }
            }}
            onClick={() => setServicesOpen(false)}
          >{t.nav.services}<span className="nav-chevron" aria-hidden="true">⌄</span></a>
          {sectionIds.slice(1).map((id) => (
            <a
              key={id}
              className={currentItem === id ? 'is-active' : ''}
              data-nav-id={id}
              aria-current={currentItem === id ? 'location' : undefined}
              href={homeLink(`#${id}`)}
              onMouseEnter={() => setServicesOpen(false)}
              onFocus={() => setServicesOpen(false)}
            >{t.nav[id]}</a>
          ))}
          <Link
            className={currentItem === 'registration' ? 'is-active' : ''}
            data-nav-id="registration"
            aria-current={currentItem === 'registration' ? 'page' : undefined}
            to="/company/registration"
            onMouseEnter={() => setServicesOpen(false)}
            onFocus={() => setServicesOpen(false)}
          >{t.nav.registration}</Link>
          <Link
            className={location.pathname === '/pricing' ? 'is-active' : ''}
            aria-current={location.pathname === '/pricing' ? 'page' : undefined}
            to="/pricing"
            onMouseEnter={() => setServicesOpen(false)}
            onFocus={() => setServicesOpen(false)}
          >{locale === 'fa' ? 'تعرفه' : 'Pricing'}</Link>
        </nav>
        <div className="header-actions">
          <div className="header-preferences">
            {themeControl}
            <button
              className="language-switch"
              type="button"
              onClick={() => onLocaleChange(locale === 'fa' ? 'en' : 'fa')}
              aria-label={locale === 'fa' ? 'Switch to English' : 'تغییر زبان به فارسی'}
            ><GlobeIcon /><span>{locale === 'fa' ? 'EN' : 'فا'}</span></button>
          </div>
          <a className="header-phone" href="tel:+982636631550" aria-label={locale === 'fa' ? 'تلفن دفتر: 02636631550' : 'Office phone: +98 26 3663 1550'}>
            <span className="header-phone__icon"><PhoneIcon /></span>
            <span className="header-phone__text"><small>{locale === 'fa' ? 'تلفن دفتر' : 'Office phone'}</small><b dir="ltr">02636631550</b></span>
          </a>
          <a className="header-cta" href={'/start-project'}>{t.nav.contact}<ArrowIcon /></a>
          <button
            className="menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? (locale === 'fa' ? 'بستن منو' : 'Close menu') : (locale === 'fa' ? 'باز کردن منو' : 'Open menu')}
            onClick={() => setMenuOpen((open) => !open)}
          >{menuOpen ? <CloseIcon /> : <MenuIcon />}</button>
        </div>
        <div id="services-dropdown" className={`services-popover ${servicesOpen ? 'is-open' : ''}`} aria-hidden={!servicesOpen} onMouseEnter={() => setServicesOpen(true)}>
          <div className="services-popover__heading"><span>{t.nav.services}</span><small>{locale === 'fa' ? 'مسیرهای همکاری با پایه‌تک' : 'Ways to work with Paye Tech'}</small></div>
          <div className="services-popover__grid">
            {services.map((service, index) => (
              <a key={service.id} href={service.id === 'web' ? '/services/web-design' : homeLink(`#service-${service.id}`)} onClick={() => setServicesOpen(false)}>
                <span className="services-popover__index">{String(index + 1).padStart(2, '0')}</span>
                <strong>{service.title[locale]}</strong>
                <small>{service.body[locale]}</small>
                <ArrowIcon />
              </a>
            ))}
          </div>
        </div>
      </header>
      <div id="mobile-menu" className={`mobile-menu ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <div className="mobile-menu__actions">
          <a href={'/start-project'} onClick={closeMenu}>{t.nav.contact}<ArrowIcon /></a>
          <a href={homeLink('#work')} onClick={closeMenu}>{t.nav.work}<ArrowIcon /></a>
        </div>
        <nav aria-label={locale === 'fa' ? 'ناوبری موبایل' : 'Mobile navigation'}>
          <a href="/" onClick={closeMenu}>{locale === 'fa' ? 'خانه' : 'Home'}</a>
          <div className="mobile-menu__service-group">
            <span>{t.nav.services}</span>
            {services.map((service) => <a key={service.id} href={service.id === 'web' ? '/services/web-design' : homeLink(`#service-${service.id}`)} onClick={closeMenu}>{service.title[locale]}<ArrowIcon /></a>)}
          </div>
          <a href={homeLink('#work')} onClick={closeMenu}>{t.nav.work}</a>
          <a href={homeLink('#process')} onClick={closeMenu}>{t.nav.process}</a>
          <a href={homeLink('#about')} onClick={closeMenu}>{t.nav.about}</a>
          <a href={homeLink('#location')} onClick={closeMenu}>{t.nav.location}</a>
          <Link to="/company/registration" onClick={closeMenu}>{t.nav.registration}</Link>
          <Link to="/pricing" onClick={closeMenu}>{locale === 'fa' ? 'تعرفه' : 'Pricing'}</Link>
        </nav>
        <div className="mobile-menu__footer">
          <a href="tel:+982636631550" dir="ltr">02636631550</a>
          <button type="button" onClick={() => onLocaleChange(locale === 'fa' ? 'en' : 'fa')}><GlobeIcon />{locale === 'fa' ? 'English' : 'فارسی'}</button>
        </div>
      </div>
    </>
  )
}
